import IndicatorButton from 'components/indicator/IndicatorButton';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import config from 'config/appConfig';
import { DivConst } from 'constants/divConst';
import { AppHelper } from 'helpers/appHelper';
import { DiagnosisHelper } from 'helpers/diagnosisHelper';
import { TermsHelper } from 'helpers/termsHelper';
import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { FormProvider, get, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useRecoilState } from 'recoil';
import { AcctState } from 'stores/atoms/acctState';
import { DiagnosisResponseType } from 'types/api/diagnosisResponseType';
import { DiagnosisEditFormStateType } from 'types/page/diagnosisEditFormStateType';
import WsModalContentConfirm from './component/modal/WsModalContentConfirm';
import WsModalContentPublish from './component/modal/WsModalContentPublish';
import WsContentEditBase from './component/pages/contentEdit/WsContentEditBase';
import WsContentEditDesign from './component/pages/contentEdit/WsContentEditDesign';
import WsContentEditReport from './component/pages/contentEdit/WsContentEditReport';
import WsContentEditRespondentFields from './component/pages/contentEdit/WsContentEditRespondentFields';
import WsDiagnosisEditQuestions from './component/pages/diagnosisEdit/WsDiagnosisEditQuestions';
import WsDiagnosisEditResultOptions from './component/pages/diagnosisEdit/WsDiagnosisEditResultOptions';
import WsDiagnosisEditResults from './component/pages/diagnosisEdit/WsDiagnosisEditResults';
import WsDiagnosisEditStatusInfo from './component/pages/diagnosisEdit/WsDiagnosisEditStatusInfo';

/**
 *　診断作成・編集画面
 */
export default function WsDiagnosis(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const { diagnosisCd } = useParams();

  const [acctState] = useRecoilState(AcctState);

  const { updateAcctState, noticeMessageSuccess, noticeMessageFailure, confirm } = useAppState();
  const {
    postDiagnosisRef,
    postDiagnosisPublish,
    postDiagnosisPublishTerminate,
    postDiagnosisDelete,
    postDiagnosisUpdate,
    postDiagnosisAiQuestions,
    postDiagnosisRegister,
  } = useApiState();

  // ローディングの制御設定
  const [initialized, setInitialized] = useState(false);
  const [processingOnSave, setProcessingOnSave] = useState(false);
  const [processingOnPreview, setProcessingOnPreview] = useState(false);
  const [processingOnPublish, setProcessingOnPublish] = useState(false);
  const [processingOnPublishTerminate, setProcessingOnPublishTerminate] = useState(false);
  const [processingOnReserveCancel, setProcessingOnReserveCancel] = useState(false);
  const [processingOnDelete, setProcessingOnDelete] = useState(false);
  const [processingOnCreateQuestion, setProcessingOnCreateQuestion] = useState(false);

  // 診断情報
  const [diagnosisState, setDiagnosisState] = useState<DiagnosisResponseType>(null);
  const [modalPublishOpenState, setModalPublishOpenState] = useState<boolean>(false);
  const [modalConfirmOpenState, setModalConfirmOpenState] = useState<boolean>(false);
  const [modalConfirmTitle, setModalConfirmTitle] = useState<string>('');

  // フォーム設定
  const useFormMethods = useForm<DiagnosisEditFormStateType>();
  const { clearErrors, control, getValues, handleSubmit, setError, setValue } = useFormMethods;
  const questionFieldArray = useFieldArray({
    control,
    name: 'questions',
  });
  const { append: appendQuestion } = questionFieldArray;
  const respondentFieldArray = useFieldArray({
    control,
    name: 'respondentFields',
  });

  // バリデーションメッセージ
  const validAlertMessage = '入力に誤りがあります。赤文字で表示されている箇所を確認してください。';
  // プラン（利用期間切れ）
  const isExpiredPlan =
    acctState.company.plan.planTypeDiv === DivConst.items.planType.expiredTrial.value ||
    acctState.company.plan.planTypeDiv === DivConst.items.planType.expiredBasic.value;
  // 公開可能
  const canPublished =
    acctState.company.plan.planTypeDiv === DivConst.items.planType.basic.value ||
    (acctState.company.plan.planTypeDiv === DivConst.items.planType.trial.value &&
      acctState.activeContent.publishingCount.filter(
        e => e.contentsTypeDiv === DivConst.items.contentsType.diagnosis.value
      )[0].count < 1);

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        await _initState();
      } catch (e) {
        showBoundary(e);
      } finally {
        setInitialized(true);
      }
    })();
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　保存ボタン押下時の処理
   */
  const onSave = async () =>
    AppHelper.submit(
      processingOnSave,
      setProcessingOnSave,
      handleSubmit(async () => {
        try {
          const response = await _save(
            diagnosisState?.diagnosisStatusDiv || DivConst.items.diagnosisStatus.unpublished.value
          );
          setDiagnosisState(response);

          // 処理完了メッセージの表示
          noticeMessageSuccess('診断型コンテンツの保存が完了しました。');
        } catch (e) {
          showBoundary(e);
        }
      })
    );
  /**
   *　プレビューボタン押下時の処理
   */
  const onPreview = async () =>
    AppHelper.submit(
      processingOnPreview,
      setProcessingOnPreview,
      handleSubmit(
        async () => {
          try {
            let diagnosis = diagnosisState;

            // 必須検証の追加実施
            if (!_validRequiredControllers()) {
              noticeMessageFailure(validAlertMessage);
              return;
            }

            // 未稼働の場合は保存処理を実施
            if (_isUnpublished()) {
              // 診断情報の更新
              diagnosis = await _save(DivConst.items.diagnosisStatus.unpublished.value);
              // 診断型コンテンツ公開APIの呼び出し
              diagnosis = await postDiagnosisPublish({
                diagnosisId: diagnosis.id,
                mode: 'preview',
              });
              setDiagnosisState(diagnosis);

              // 保存後に自動でプレビュー画面を開くとポップアップブロック判定されるため、ユーザーにアクションを促す
              confirm({
                title: '診断型コンテンツの保存が完了しました',
                message: `このままプレビュー画面を開きますか？`,
                onSubmit: async () => {
                  DiagnosisHelper.openPreviewUrl(diagnosis.previewCd);
                },
              });
            } else {
              // プレビュー画面を開く
              DiagnosisHelper.openPreviewUrl(diagnosis.previewCd);
            }
          } catch (e) {
            showBoundary(e);
          }
        },
        // バリデーションエラーがある場合の処理
        () => {
          _validRequiredControllers();
          noticeMessageFailure(validAlertMessage);
        }
      )
    );

  /**
   *　公開ボタン押下時の処理
   */
  const onPublishModalOpen = async () =>
    AppHelper.submit(
      processingOnPublish,
      setProcessingOnPublish,
      handleSubmit(
        async () => {
          try {
            // 未設定項目確認モーダルを開く
            if (!getValues().privacyPolicyUrl) {
              setModalConfirmTitle('プライバシーポリシーが未登録です');
              setModalConfirmOpenState(true);
              return;
            }
            if (!getValues().termsUrl) {
              setModalConfirmTitle('利用規約が未登録です');
              setModalConfirmOpenState(true);
              return;
            }
            if (!getValues().copyright) {
              setModalConfirmTitle('コピーライトが未登録です');
              setModalConfirmOpenState(true);
              return;
            }
            if (!getValues().reportMailReplyAddress) {
              setModalConfirmTitle('返信元メールアドレスが未登録です');
              setModalConfirmOpenState(true);
              return;
            }

            // 必須検証の追加実施
            if (!_validRequiredControllers()) {
              noticeMessageFailure(validAlertMessage);
              return;
            }
            // 診断型コンテンツ公開設定モーダルを開く
            setModalPublishOpenState(true);
          } catch (e) {
            showBoundary(e);
          }
        },
        // バリデーションエラーがある場合の処理
        () => {
          _validRequiredControllers();
          noticeMessageFailure(validAlertMessage);
        }
      )
    );

  /**
   *　未設定項目確認モーダルのOKボタンがクリックされた時の処理
   */
  const onConfirmModalSubmit = () => {
    setModalConfirmOpenState(false);
    AppHelper.submit(
      processingOnSave,
      setProcessingOnSave,
      handleSubmit(async () => {
        try {
          const response = await _save(
            diagnosisState?.diagnosisStatusDiv || DivConst.items.diagnosisStatus.unpublished.value
          );
          setDiagnosisState(response);
          navigate(`/setting/${acctState.company.cd}/company`);
        } catch (e) {
          showBoundary(e);
        }
      })
    );
  };

  /**
   *　公開設定完了時の処理。
   */
  const onPublishModalSubmit = async (attr: {
    publishStartAt: number;
    publishEndAt: number;
    noIndexFlag: boolean;
  }) => {
    try {
      // ローディングの表示
      setProcessingOnPublish(true);
      // 固定項目
      setValue('publishStartAt', attr.publishStartAt);
      setValue('publishEndAt', attr.publishEndAt);
      setValue('noIndexFlag', attr.noIndexFlag);

      // 診断情報の更新
      let response = await _save(DivConst.items.diagnosisStatus.reserved.value);

      // 即時公開の場合の処理
      if (attr.publishStartAt == null) {
        // 診断型コンテンツ公開APIの呼び出し
        response = await postDiagnosisPublish({
          diagnosisId: response.id,
          mode: 'production',
        });
        updateAcctState(['activeContent', 'usage'], acctState.company.cd);
      }
      setDiagnosisState(response);
      // ローディングの停止
      setProcessingOnPublish(false);
    } catch (e) {
      showBoundary(e);
    }
  };

  /**
   *　公開停止ボタン押下時の処理
   */
  const onPublishTerminate = async () => {
    try {
      // ローディングの表示
      setProcessingOnPublishTerminate(true);
      // 診断型コンテンツ公開停止APIの呼び出し
      setDiagnosisState(
        await postDiagnosisPublishTerminate({
          diagnosisId: diagnosisState.id,
          mode: 'production',
        })
      );
      // 画面の状態を更新
      updateAcctState(['activeContent', 'usage'], acctState.company.cd);
      // ローディングの停止
      setProcessingOnPublishTerminate(false);
    } catch (e) {
      showBoundary(e);
    }
  };

  /**
   *　予約取消ボタン押下時の処理
   */
  const onReserveCancel = async () => {
    try {
      // ローディングの表示
      setProcessingOnReserveCancel(true);
      // 診断型コンテンツ情報の更新
      const response = await _save(DivConst.items.diagnosisStatus.unpublished.value);
      setDiagnosisState(response);
      // ローディングの停止
      setProcessingOnReserveCancel(false);
    } catch (e) {
      showBoundary(e);
    }
  };

  /**
   *　削除ボタン押下時の処理
   */
  const onDelete = async () => {
    try {
      // ローディングの表示
      setProcessingOnDelete(true);
      // 診断情報の更新
      await postDiagnosisDelete({ diagnosisId: diagnosisState.id });
      // 画面の状態を更新
      updateAcctState(['activeContent', 'usage'], acctState.company.cd);
      // ローディングの停止
      setProcessingOnDelete(false);
      // コンテンツ画面に遷移
      navigate(`/ws/${acctState.company.cd}/content/search`);
    } catch (e) {
      showBoundary(e);
    }
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　画面の状態を初期化します。
   */
  const _initState = async () => {
    // 固定項目
    setValue('privacyPolicyUrl', acctState.company.privacyPolicyUrl);
    setValue('reportMailSenderAddress', config.ses.from.address);
    setValue('reportMailReplyAddress', acctState.company.reportMailReplyAddress);
    setValue('copyright', acctState.company.copyright);
    setValue('publishStartAt', null);
    setValue('publishEndAt', null);
    setValue('noIndexFlag', false);
    setValue('diagnosisTemplateId', 1);
    setValue(
      'termsUrl',
      acctState.company.termsInputDiv === DivConst.items.termsInputType.url.value
        ? acctState.company.termsUrl
        : TermsHelper.getProductionUrl(acctState.company.cd)
    );

    // 新規登録の場合
    if (!diagnosisCd) {
      setValue('reportMailSenderName', acctState.company.reportMailSenderName);
      setValue('reportMailSignatureHtml', acctState.company.reportMailSignatureHtml);
      setValue('respondentFieldGroupId', null);
      setValue('respondentFields', []);
      setValue('questions', [
        {
          options: [{}, {}, {}, {}],
        },
      ]);
      setValue('results', [
        { id: Math.random(), isNew: true },
        { id: Math.random(), isNew: true },
        { id: Math.random(), isNew: true },
      ]);
    }
    // 更新の場合
    else {
      const response = await postDiagnosisRef({
        diagnosisCd: diagnosisCd,
      });

      setDiagnosisState(response);

      setValue('title', response.title);
      setValue('titleHiddenFlag', response.titleHiddenFlag);
      setValue('description', response.description);
      setValue('respondentMailDiv', response.respondentMailDiv);
      setValue('imageKey', response.imageKey);
      setValue('reportMailSubject', response.reportMailSubject);
      setValue('reportMailSenderName', response.reportMailSenderName);
      setValue('reportMailSignatureHtml', response.reportMailSignatureHtml);
      setValue('reportMailDescribeHtml', response.reportMailDescribeHtml);
      setValue('diagnosisTemplateId', response.diagnosisTemplateId);
      setValue('diagnosisTemplateId', response.diagnosisTemplateId);
      setValue('publishStartAt', response.publishStartAt);
      setValue('publishEndAt', response.publishEndAt);
      setValue('noIndexFlag', response.noIndexFlag);
      setValue('benchmarkContactListId', response.benchmarkContactListId);
      setValue('gtmId', response.gtmId);
      setValue('respondentFieldGroupId', response.respondentFieldGroupId);
      setValue(
        'respondentFields',
        response.respondentFields.map(field => {
          return {
            id: field.id,
            no: field.no,
            getFlag: field.getFlag,
            respondentFieldDiv: field.respondentFieldDiv,
            respondentStandardFieldDiv: field.respondentStandardFieldDiv,
            label: field.label,
            placeholder: field.placeholder,
            respondentCustomFieldDiv: field.respondentCustomFieldDiv,
            respondentCustomFieldInputDiv: field.respondentCustomFieldInputDiv,
            maxLength: field.maxLength,
          };
        })
      );
      setValue(
        'lineFlag',
        response.lineFlag
          ? DivConst.items.lineFlag.enable.value
          : DivConst.items.lineFlag.disable.value
      );
      setValue('lineUrl', response.lineUrl);
      setValue(
        'questions',
        response.questions.map(question => {
          return {
            title: question.title,
            questionImageKey: question.questionImageKey,
            options: question.options.map(option => {
              return {
                title: option.title,
                diagnosisResultList: option.diagnosisResultList,
              };
            }),
          };
        })
      );
      setValue(
        'results',
        response.results.map(result => {
          return {
            id: result.id,
            title: result.title,
            descriptionHtml: result.descriptionHtml,
            imageKey: result.imageKey,
          };
        })
      );
    }
  };

  /**
   *　診断情報を保存します。
   */
  const _save = async (diagnosisStatusDiv: number): Promise<DiagnosisResponseType> => {
    let response: DiagnosisResponseType = null;
    // 新規登録の場合
    if (!diagnosisState) {
      response = await postDiagnosisRegister({
        companyId: acctState.company.id,
        diagnosisStatusDiv: diagnosisStatusDiv,
        ..._convFormValuesToParam(),
      });
    }
    // 更新の場合
    else {
      response = await postDiagnosisUpdate({
        diagnosisId: diagnosisState.id,
        diagnosisStatusDiv: diagnosisStatusDiv,
        respondentFieldGroupId: diagnosisState.respondentFieldGroupId,
        ..._convFormValuesToParam(),
      });
    }
    // 画面の状態を更新
    updateAcctState(['activeContent'], acctState.company.cd);

    return response;
  };

  /**
   *　フォーム入力値をパラメタに変換します。
   */
  const _convFormValuesToParam = () => {
    const values = getValues();

    return {
      title: values.title,
      titleHiddenFlag: values.titleHiddenFlag,
      description: values.description,
      respondentMailDiv: values.respondentMailDiv,
      imageKey: values.imageKey,
      reportMailSubject: values.reportMailSubject,
      reportMailSenderName: values.reportMailSenderName,
      reportMailSignatureHtml: values.reportMailSignatureHtml,
      reportMailDescribeHtml: values.reportMailDescribeHtml,
      diagnosisTemplateId: values.diagnosisTemplateId,
      publishStartAt: values.publishStartAt,
      publishEndAt: values.publishEndAt,
      noIndexFlag: values.noIndexFlag,
      benchmarkContactListId: values.benchmarkContactListId,
      gtmId: values.gtmId,
      respondentFields: values.respondentFields.map(field => {
        return {
          getFlag: field.getFlag,
          respondentFieldDiv: field.respondentFieldDiv,
          respondentStandardFieldDiv: field.respondentStandardFieldDiv,
          label: field.label,
          placeholder: field.placeholder,
          respondentCustomFieldDiv: field.respondentCustomFieldDiv,
          respondentCustomFieldInputDiv: field.respondentCustomFieldInputDiv,
          maxLength: field.maxLength,
        };
      }),
      lineFlag: Number(values.lineFlag) === DivConst.items.lineFlag.enable.value,
      lineUrl: values.lineUrl,
      questions: values.questions.map(question => {
        return {
          title: question.title,
          questionImageKey: question.questionImageKey || null,
          options: question.options.map(option => {
            return {
              title: option.title,
              diagnosisResultList: DiagnosisHelper.convResultListIdToTitle(
                option.diagnosisResultList,
                values.results.map(result => {
                  return {
                    id: result.id,
                    title: result.title,
                    descriptionHtml: result.descriptionHtml,
                    imageKey: result.imageKey,
                  };
                })
              ),
            };
          }),
        };
      }),
      results: values.results.map(result => {
        return {
          title: result.title,
          descriptionHtml: result.descriptionHtml,
          imageKey: result.imageKey || null,
        };
      }),
    };
  };

  /**
   *　公開時に必須となる項目を検証します。
   */
  const _validRequiredControllers = (): boolean => {
    const values = getValues();
    let isValid = true;

    if (!values.privacyPolicyUrl) {
      setError('privacyPolicyUrl', {
        type: 'custom',
        message: '必須項目です。カンパニー設定画面から登録してください。',
      });
      isValid = false;
    }

    isValid = _validRequired(values, 'description') && isValid;
    isValid = _validRequiredHtml(values, 'reportMailSignatureHtml') && isValid;
    isValid = _validRequired(values, 'diagnosisTemplateId') && isValid;
    isValid = _validRespondentFields(values) && isValid;
    isValid = _validLineUrl(values) && isValid;

    values.questions.forEach((question, questionIndex) => {
      isValid = _validRequired(values, `questions.${questionIndex}.title`) && isValid;

      question.options.forEach((option, optionIndex) => {
        isValid =
          _validRequired(values, `questions.${questionIndex}.options.${optionIndex}.title`) &&
          isValid;
      });
    });

    values.results.forEach((result, resultIndex) => {
      isValid = _validRequired(values, `results.${resultIndex}.title`) && isValid;
      isValid = _validRequiredHtml(values, `results.${resultIndex}.descriptionHtml`) && isValid;
    });

    return isValid;
  };

  /**
   *　フォームの必須検証をします。
   */
  const _validRequired = (values: DiagnosisEditFormStateType, name: string): boolean => {
    const value = get(values, name);

    if (AppHelper.isEmpty(value)) {
      setError(name as any, {
        type: 'custom',
        message: '必須項目です。',
      });
      return false;
    }
    return true;
  };

  /**
   *　HTMLエディタの追加必須検証をします。
   */
  const _validRequiredHtml = (values: DiagnosisEditFormStateType, name: string): boolean => {
    const value = get(values, name);

    if (AppHelper.isEmpty(value) || AppHelper.isEmpty(value.replace(/(<([^>]+)>)/gi, ''))) {
      setError(name as any, {
        type: 'custom',
        message: '必須項目です。',
      });
      return false;
    }
    return true;
  };

  /**
   * 取得項目を検証します。
   */
  const _validRespondentFields = (values: DiagnosisEditFormStateType): boolean => {
    // 項目取得なしの場合はチェック不要
    if (Number(values.respondentMailDiv) === DivConst.items.diagnosisRespondentMail.none.value) {
      clearErrors('respondentMailDiv');
      return true;
    }

    // 少なくとも1つの項目が取得対象になっているかチェック
    const hasGetFlag = values.respondentFields.some(field => field.getFlag);
    if (!hasGetFlag) {
      setError('respondentMailDiv', {
        type: 'custom',
        message: '取得対象が選択されていません。少なくとも1つの項目を取得対象にしてください。',
      });
      return false;
    }

    return true;
  };

  /**
   * 取得項目を検証します。
   */
  const _validLineUrl = (values: DiagnosisEditFormStateType): boolean => {
    // LINE連携なしの場合はチェック不要
    if (Number(values.lineFlag) === DivConst.items.lineFlag.disable.value) {
      clearErrors('lineUrl');
      return true;
    }

    if (AppHelper.isEmpty(values.lineUrl)) {
      setError('lineUrl', {
        type: 'custom',
        message: '必須項目です。',
      });
      return false;
    }
    return true;
  };

  /**
   *　読み取り専用かどうかを判定します。
   */
  const _isReadOnly = (): boolean => {
    return (
      diagnosisState != null &&
      diagnosisState?.diagnosisStatusDiv != DivConst.items.diagnosisStatus.unpublished.value
    );
  };

  /**
   *　未稼働かどうかを判定します。
   */
  const _isUnpublished = (): boolean => {
    return (
      diagnosisState == null ||
      diagnosisState.diagnosisStatusDiv == DivConst.items.diagnosisStatus.unpublished.value
    );
  };

  /**
   *　AIによる診断作成を行います。
   */
  const onCreateDiagnosis = () => {
    AppHelper.submit(processingOnCreateQuestion, setProcessingOnCreateQuestion, async () => {
      try {
        const values = getValues();
        let isValid = true;

        clearErrors();

        if (AppHelper.isEmpty(values.title)) {
          setError('title' as any, {
            type: 'required',
          });
          isValid = false;
        }
        if (AppHelper.isEmpty(values.description)) {
          setError('description' as any, {
            type: 'required',
          });
          isValid = false;
        }
        if (!isValid) {
          return;
        }

        const response = await postDiagnosisAiQuestions({
          companyId: acctState.company.id,
          title: values.title || '',
          description: values.description || '',
        });

        // 先に配列を空にする
        setValue('questions', []);
        setValue('results', []);

        // 新しいデータで配列を更新
        setTimeout(() => {
          // 新規の診断結果にIDをマッピング
          const results = response.results.map(result => {
            return {
              id: Math.random(),
              title: result.title,
              descriptionHtml: result.descriptionHtml,
              imageKey: null,
              isNew: true,
            };
          });

          for (const question of response.questions) {
            const options = question.options.map(option => {
              return {
                title: option.title,
                diagnosisResultList: DiagnosisHelper.convResultListTitleToId(
                  option.diagnosisResultList,
                  results
                ),
              };
            });
            appendQuestion({
              title: question.title,
              questionImageKey: null,
              options: options,
            });
          }
          setValue('results', results);

          noticeMessageSuccess('AIによる質問生成が完了しました。');
        }, 300);
      } catch (e) {
        showBoundary(e);
      }
    });
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <FormProvider {...useFormMethods}>
        {/* 初期化中の表示 */}
        <IndicatorScreen visible={!initialized || processingOnPublish} />
        {/* 初期化後の表示 */}
        {initialized && (
          <>
            <section className="section-header">
              <div className="container">
                <a onClick={() => navigate(-1)} className="btn-back-list mt-0 mb-3">
                  戻る
                </a>
                <div className="d-md-flex d-flex-header">
                  <div className="flex-shrink-0 flex-left">
                    <h2 className="flex-title">診断型コンテンツ作成・編集</h2>
                  </div>
                  <div className="flex-grow-1 flex-right">
                    <ul className="list-inline list-btn mb-0">
                      {/* 保存ボタン */}
                      {_isUnpublished() && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onSave}
                            disabled={processingOnSave}
                            className="btn btn-common green w-100"
                          >
                            <IndicatorButton visible={processingOnSave} />
                            保存する
                          </button>
                        </li>
                      )}

                      {/* 公開停止ボタン */}
                      {diagnosisState?.diagnosisStatusDiv ==
                        DivConst.items.diagnosisStatus.published.value && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onPublishTerminate}
                            disabled={processingOnPublishTerminate}
                            className="btn btn-common btn-danger w-100"
                          >
                            <IndicatorButton visible={processingOnPublishTerminate} />
                            公開を停止する
                          </button>
                        </li>
                      )}
                      {/* 予約停止ボタン */}
                      {diagnosisState?.diagnosisStatusDiv ==
                        DivConst.items.diagnosisStatus.reserved.value && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onReserveCancel}
                            disabled={processingOnReserveCancel}
                            className="btn btn-common btn-danger w-100"
                          >
                            <IndicatorButton visible={processingOnReserveCancel} />
                            予約を取り消す
                          </button>
                        </li>
                      )}
                      {/* プレビューボタン */}
                      <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                        <button
                          onClick={onPreview}
                          disabled={processingOnPreview}
                          className="btn btn-common outline-blue w-100"
                        >
                          <IndicatorButton visible={processingOnPreview} />
                          {`${_isUnpublished() ? '保存して' : ''}プレビュー`}
                        </button>
                      </li>
                      {/* 公開ボタン */}
                      {_isUnpublished() && (
                        <li className="list-inline-item list-item-lg ms-lg-4 mb-2">
                          <div
                            data-tooltip-id="custom-tooltip"
                            data-tooltip-content={
                              isExpiredPlan
                                ? 'プランの期限がきれました。プランのアップグレードをご検討ください。'
                                : 'トライアルプランの公開は1つまでです。他の診断を一度非公開にするかプランのアップグレードをご検討ください。'
                            }
                            className="tooltip-wrapper"
                          >
                            <button
                              onClick={onPublishModalOpen}
                              disabled={isExpiredPlan || !canPublished}
                              className="btn btn-common moderate-blue w-100"
                            >
                              公開する
                            </button>
                          </div>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
                {/* 削除ボタン */}
                {_isUnpublished() && diagnosisState && (
                  <div className="d-md-flex d-flex-header mt-2">
                    <div className="flex-shrink-0 flex-left"></div>
                    <div className="flex-grow-1 flex-right">
                      <ul className="list-inline list-btn mb-0">
                        <li className="list-inline-item list-item-lg ms-lg-4 pe-1">
                          <a
                            onClick={() =>
                              confirm({
                                title: '診断型コンテンツの削除',
                                message:
                                  '診断型コンテンツや獲得リストなどの関連データを全て削除します。この操作は取り消せません。',
                                onSubmit: onDelete,
                              })
                            }
                            className="text-danger"
                          >
                            <IndicatorButton visible={processingOnDelete} />
                            削除する
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* ステータスや公開情報の表示 */}
                <WsDiagnosisEditStatusInfo diagnosis={diagnosisState} />
              </div>
            </section>

            <section className="section-header"></section>

            {/* 基本設定 */}
            <WsContentEditBase
              readOnly={_isReadOnly()}
              contentCategory={DivConst.items.contentsType.diagnosis}
              onCreate={onCreateDiagnosis}
              processing={processingOnCreateQuestion}
            />
            {/* 取得項目設定 */}
            <WsContentEditRespondentFields
              readOnly={_isReadOnly()}
              contentCategory={DivConst.items.contentsType.diagnosis}
              respondentFieldArray={respondentFieldArray}
            />
            {/* デザイン設定 */}
            <WsContentEditDesign
              readOnly={_isReadOnly()}
              contentCategory={DivConst.items.contentsType.diagnosis}
            />
            {/* 判定結果 */}
            <WsDiagnosisEditResultOptions readOnly={_isReadOnly()} />
            {/* 設問設定 */}
            <WsDiagnosisEditQuestions readOnly={_isReadOnly()} fieldArray={questionFieldArray} />
            {/* 判定結果 */}
            <WsDiagnosisEditResults readOnly={_isReadOnly()} />
            {/* レポートメール設定 */}
            <WsContentEditReport
              readOnly={_isReadOnly()}
              contentCategory={DivConst.items.contentsType.diagnosis}
            />

            <section className="section-header">
              <div className="container">
                <div className="d-md-flex d-flex-header">
                  <div className="flex-shrink-0 flex-left"></div>
                  <div className="flex-grow-1 flex-right">
                    <ul className="list-inline list-btn mb-0">
                      {/* 保存ボタン */}
                      {_isUnpublished() && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onSave}
                            disabled={processingOnSave}
                            className="btn btn-common green w-100"
                          >
                            <IndicatorButton visible={processingOnSave} />
                            保存する
                          </button>
                        </li>
                      )}

                      {/* 公開停止ボタン */}
                      {diagnosisState?.diagnosisStatusDiv ==
                        DivConst.items.diagnosisStatus.published.value && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onPublishTerminate}
                            disabled={processingOnPublishTerminate}
                            className="btn btn-common btn-danger w-100"
                          >
                            <IndicatorButton visible={processingOnPublishTerminate} />
                            公開を停止する
                          </button>
                        </li>
                      )}
                      {/* 予約停止ボタン */}
                      {diagnosisState?.diagnosisStatusDiv ==
                        DivConst.items.diagnosisStatus.reserved.value && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onReserveCancel}
                            disabled={processingOnReserveCancel}
                            className="btn btn-common btn-danger w-100"
                          >
                            <IndicatorButton visible={processingOnReserveCancel} />
                            予約を取り消す
                          </button>
                        </li>
                      )}
                      {/* プレビューボタン */}
                      <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                        <button
                          onClick={onPreview}
                          disabled={processingOnPreview}
                          className="btn btn-common outline-blue w-100"
                        >
                          <IndicatorButton visible={processingOnPreview} />
                          {`${_isUnpublished() ? '保存して' : ''}プレビュー`}
                        </button>
                      </li>
                      {/* 公開ボタン */}
                      {_isUnpublished() && (
                        <li className="list-inline-item list-item-lg ms-lg-4 mb-2">
                          <div
                            data-tooltip-id="custom-tooltip"
                            data-tooltip-content={
                              isExpiredPlan
                                ? 'プランの期限がきれました。プランのアップグレードをご検討ください。'
                                : 'トライアルプランの公開は1つまでです。他の診断を一度非公開にするかプランのアップグレードをご検討ください。'
                            }
                            className="tooltip-wrapper"
                          >
                            <button
                              onClick={onPublishModalOpen}
                              disabled={isExpiredPlan || !canPublished}
                              className="btn btn-common moderate-blue w-100"
                            >
                              公開する
                            </button>
                          </div>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </FormProvider>

      {/* 診断型コンテンツ公開設定モーダル */}
      <WsModalContentPublish
        open={modalPublishOpenState}
        setOpen={setModalPublishOpenState}
        publishStartAt={diagnosisState?.publishStartAt}
        publishEndAt={diagnosisState?.publishEndAt}
        noIndexFlag={diagnosisState?.noIndexFlag}
        onSubmit={attr => onPublishModalSubmit(attr)}
      />
      {
        // トライアルプラン（利用期間切れ）もしくは公開上限の場合以外は表示しない
        (isExpiredPlan || !canPublished) && (
          <Tooltip id="custom-tooltip" place="top" variant="info" />
        )
      }

      {/* 未設定確認モーダル */}
      <WsModalContentConfirm
        open={modalConfirmOpenState}
        setOpen={setModalConfirmOpenState}
        title={modalConfirmTitle}
        onSubmit={onConfirmModalSubmit}
      />
    </>
  );
}
